import firebase from "firebase/app";
import "firebase/auth";
import dispatch, { MiddlewareAction } from "../middleware";
import { getAdmin, getLenderSources } from "./admin.action";
import { getApplicationDocTypes } from "./application.action";
import { getUserDocTypes } from "./user.action";

export enum Actions {
  FIREBASE_LOGIN = "FIREBASE_LOGIN",
  FIREBASE_REGISTER = "FIREBASE_REGISTER",
  CLEARE_FIREBASE_RESPONSE = "CLEARE_FIREBASE_RESPONSE",
  FIREBASE_VERIFICATION_MAIL = "FIREBASE_VERIFICATION_MAIL",
  FIREBASE_PASSWORD_UPDATE = "FIREBASE_PASSWORD_UPDATE",
  STOP_LOADING = "STOP_LOADING",
  REQUEST_USER_LOGOUT = "REQUEST_USER_LOGOUT",
  MFA_ENROLL = "MFA_ENROLL",
  MFA_CHALLENGE = "MFA_CHALLENGE",
  MFA_VERIFY = "MFA_VERIFY",
  MFA_ENROLLMENT_REQUIRED = "MFA_ENROLLMENT_REQUIRED",
}

let mfaResolver: any = null;

export function getMfaResolver() {
  return mfaResolver;
}

export function clearMfaResolver() {
  mfaResolver = null;
}

export function login(email: string, password: string, storeDispatch: any) {
  dispatch(storeDispatch, firebaseLogin());
  firebase
    .auth()
    .signInWithEmailAndPassword(email, password)
    .then(async (response: any) => {
      if (!response.user.emailVerified) {
        dispatch(
          storeDispatch,
          firebaseLogin("FAILURE", {
            message: "You haven't verified your email.",
            code: "",
          })
        );
      } else {
        await doAfterLoginSuccess(storeDispatch);
      }
    })
    .catch((error) => {
      if (error.code === "auth/multi-factor-auth-required") {
        mfaResolver = error.resolver;
        dispatch(
          storeDispatch,
          firebaseLogin("MFA_REQUIRED", {
            message: "Multi-factor authentication required.",
            code: error.code,
            hints: error.resolver?.hints?.map((h: any) => ({
              uid: h.uid,
              displayName: h.displayName,
              factorId: h.factorId,
              enrollmentTime: h.enrollmentTime,
            })),
          })
        );
      } else {
        dispatch(storeDispatch, firebaseLogin("FAILURE", error));
      }
    });
}

export function firebaseLogin(
  type?: string,
  response?: object
): MiddlewareAction {
  return {
    actionName: `${Actions.FIREBASE_LOGIN}${type ? `_${type}` : ""}`,
    body: { response },
  };
}
export function firebaseRegister(
  type?: string,
  response?: object
): MiddlewareAction {
  return {
    actionName: `${Actions.FIREBASE_REGISTER}${type ? `_${type}` : ""}`,
    body: { response },
  };
}
export function register(email: string, password: string, storeDispatch: any) {
  dispatch(storeDispatch, firebaseRegister());
  firebase
    .auth()
    .createUserWithEmailAndPassword(email, password)
    .then(async (response: any) => {
      dispatch(storeDispatch, firebaseRegister("SUCCESS", response));
      sendVerificationMail(firebase.auth().currentUser, storeDispatch);
      await doAfterLoginSuccess(storeDispatch);
    })
    .catch((error) => {
      dispatch(storeDispatch, firebaseRegister("FAILURE", error));
    });
}

export function sendVerificationMail(currentUser: any, storeDispatch: any) {
  currentUser
    .sendEmailVerification()
    .then(() => {
      dispatch(storeDispatch, firebaseVerification("SUCCESS"));
    })
    .catch((error: any) => {
      dispatch(storeDispatch, firebaseVerification("FAILURE", error));
    });
}
export function firebaseVerification(
  type?: string,
  response?: object
): MiddlewareAction {
  return {
    actionName: `${Actions.FIREBASE_VERIFICATION_MAIL}${
      type ? `_${type}` : ""
    }`,
    body: { response },
  };
}
export function clearFirebaseResponse() {
  return { actionName: Actions.CLEARE_FIREBASE_RESPONSE };
}
export function sendResetPwEmail(email: string, storeDispatch: any) {
  dispatch(storeDispatch, firebaseVerification());
  firebase
    .auth()
    .sendPasswordResetEmail(email)
    .then(() => {
      dispatch(storeDispatch, firebaseVerification("SUCCESS"));
    })
    .catch((error) => {
      dispatch(storeDispatch, firebaseVerification("FAILURE", error));
    });
}
export function checkForAlreadyLogin(storeDispatch: any) {
  dispatch(storeDispatch, firebaseLogin());
  firebase.auth().onAuthStateChanged(async (user) => {
    if (user && user.emailVerified) {
      await doAfterLoginSuccess(storeDispatch);
    } else {
      dispatch(storeDispatch, stopLoading());
    }
  });
}

export default function logoutUser(token: string): MiddlewareAction {
  return {
    url: "/v1/users/logout",
    type: "POST",
    actionName: Actions.REQUEST_USER_LOGOUT,
    token,
  };
}

export async function logout(storeDispatch?: any) {
  try {
    // Force-refresh so the logout call itself never trips the 15-min
    // TOKEN_EXPIRED_BY_AGE check and shows the consent screen. The refresh may
    // fail (revoked token at the 2-hour hard cap, or throttled) — fall back to
    // the cached token so the server logout still runs when a token exists,
    // then sign out locally and redirect either way.
    let token = "";
    try {
      token = await getIdToken(true);
    } catch {
      try {
        token = await getIdToken();
      } catch {
        // No usable token — sign out locally and redirect anyway.
      }
    }
    await firebase.auth().signOut();
    if (token) {
      await dispatch(storeDispatch, logoutUser(token));
    }
    window.location.href = window.location.origin;
  } catch {}
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
  storeDispatch: any
) {
  await dispatch(storeDispatch, {
    actionName: Actions.FIREBASE_PASSWORD_UPDATE,
  });
  const user = firebase.auth().currentUser;
  if (!user?.email) {
    await dispatch(storeDispatch, {
      actionName: `${Actions.FIREBASE_PASSWORD_UPDATE}_FAILURE`,
    });
    throw new Error("No logged in user found");
  }
  const credential = firebase.auth.EmailAuthProvider.credential(
    user.email,
    currentPassword
  );
  try {
    await user.reauthenticateWithCredential(credential);
    await user.updatePassword(newPassword);
    await dispatch(storeDispatch, {
      actionName: `${Actions.FIREBASE_PASSWORD_UPDATE}_SUCCESS`,
    });
    return { message: "Your password changed successfully" };
  } catch (error) {
    await dispatch(storeDispatch, {
      actionName: `${Actions.FIREBASE_PASSWORD_UPDATE}_FAILURE`,
    });
    throw new Error(
      error?.message ||
        "Your password can't be changed right now, Please Try again later"
    );
  }
}
export function mfaEnrollAction(type?: string, response?: object): MiddlewareAction {
  return {
    actionName: `${Actions.MFA_ENROLL}${type ? `_${type}` : ""}`,
    body: { response },
  };
}

export function mfaChallengeAction(type?: string, response?: object): MiddlewareAction {
  return {
    actionName: `${Actions.MFA_CHALLENGE}${type ? `_${type}` : ""}`,
    body: { response },
  };
}

export function mfaVerifyAction(type?: string, response?: object): MiddlewareAction {
  return {
    actionName: `${Actions.MFA_VERIFY}${type ? `_${type}` : ""}`,
    body: { response },
  };
}

export async function sendMfaEnrollCode(
  phoneNumber: string,
  recaptchaVerifier: any,
  storeDispatch: any
) {
  dispatch(storeDispatch, mfaEnrollAction());
  const user = firebase.auth().currentUser;
  if (!user) {
    dispatch(storeDispatch, mfaEnrollAction("FAILURE", { message: "No user logged in" }));
    return;
  }
  try {
    const multiFactorSession = await (user as any).multiFactor.getSession();
    const phoneAuthProvider = new (firebase.auth as any).PhoneAuthProvider();
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(
      { phoneNumber, multiFactorSession },
      recaptchaVerifier
    );
    dispatch(storeDispatch, mfaEnrollAction("CODE_SENT", { verificationId }));
    return verificationId;
  } catch (error: any) {
    dispatch(storeDispatch, mfaEnrollAction("FAILURE", { message: error.message || "Failed to send code" }));
  }
}

export async function confirmMfaEnroll(
  verificationId: string,
  verificationCode: string,
  displayName: string,
  storeDispatch: any
): Promise<boolean> {
  dispatch(storeDispatch, mfaVerifyAction());
  const user = firebase.auth().currentUser;
  if (!user) {
    dispatch(storeDispatch, mfaVerifyAction("FAILURE", { message: "No user logged in" }));
    return false;
  }
  try {
    const phoneAuthCredential = (firebase.auth as any).PhoneAuthProvider.credential(
      verificationId,
      verificationCode
    );
    const multiFactorAssertion =
      (firebase.auth as any).PhoneMultiFactorGenerator.assertion(phoneAuthCredential);
    await (user as any).multiFactor.enroll(multiFactorAssertion, displayName);
    dispatch(storeDispatch, mfaVerifyAction("SUCCESS", { message: "MFA enrolled successfully" }));
    return true;
  } catch (error: any) {
    dispatch(storeDispatch, mfaVerifyAction("FAILURE", { message: error.message || "Verification failed" }));
    return false;
  }
}

export async function sendMfaChallengeCode(
  storeDispatch: any
) {
  dispatch(storeDispatch, mfaChallengeAction());
  const resolver = mfaResolver;
  if (!resolver) {
    dispatch(storeDispatch, mfaChallengeAction("FAILURE", { message: "No MFA resolver found" }));
    return;
  }
  try {
    const hint = resolver.hints[0];
    if (!hint) {
      dispatch(storeDispatch, mfaChallengeAction("FAILURE", { message: "No MFA hints available" }));
      return;
    }
    const verifier = (window as any).recaptchaVerifier;
    if (!verifier) {
      dispatch(storeDispatch, mfaChallengeAction("FAILURE", { message: "reCAPTCHA not initialized" }));
      return;
    }
    const phoneAuthProvider = new (firebase.auth as any).PhoneAuthProvider();
    let timeoutId: ReturnType<typeof setTimeout>;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error("Request timed out")), 30000);
    });
    const verificationId = await Promise.race([
      phoneAuthProvider.verifyPhoneNumber(
        { multiFactorHint: hint, session: resolver.session },
        verifier
      ),
      timeoutPromise,
    ]).finally(() => clearTimeout(timeoutId));
    dispatch(storeDispatch, mfaChallengeAction("CODE_SENT", { verificationId }));
    return verificationId;
  } catch (error: any) {
    dispatch(storeDispatch, mfaChallengeAction("FAILURE", { message: error.message || "Failed to send code" }));
  }
}

export async function confirmMfaChallenge(
  verificationId: string,
  verificationCode: string,
  storeDispatch: any
): Promise<boolean> {
  dispatch(storeDispatch, mfaVerifyAction());
  const resolver = mfaResolver;
  if (!resolver) {
    dispatch(storeDispatch, mfaVerifyAction("FAILURE", { message: "No MFA resolver found" }));
    return false;
  }
  try {
    const phoneAuthCredential = (firebase.auth as any).PhoneAuthProvider.credential(
      verificationId,
      verificationCode
    );
    const multiFactorAssertion =
      (firebase.auth as any).PhoneMultiFactorGenerator.assertion(phoneAuthCredential);
    const result = await resolver.resolveSignIn(multiFactorAssertion);
    if (result.user.emailVerified) {
      dispatch(storeDispatch, firebaseLogin("SUCCESS"));
      dispatch(storeDispatch, getAdmin());
      dispatch(storeDispatch, getLenderSources());
      dispatch(storeDispatch, getUserDocTypes());
      dispatch(storeDispatch, getApplicationDocTypes());
    } else {
      dispatch(
        storeDispatch,
        firebaseLogin("FAILURE", {
          message: "You haven't verified your email.",
          code: "",
        })
      );
    }
    mfaResolver = null;
    return true;
  } catch (error: any) {
    dispatch(storeDispatch, mfaVerifyAction("FAILURE", { message: error.message || "Verification failed" }));
    return false;
  }
}

export function stopLoading() {
  return { actionName: Actions.STOP_LOADING };
}
async function doAfterLoginSuccess(storeDispatch: any) {
  dispatch(storeDispatch, getAdmin());
  dispatch(storeDispatch, getLenderSources());
  dispatch(storeDispatch, getUserDocTypes());
  dispatch(storeDispatch, getApplicationDocTypes());
  await checkMfaEnrollment(storeDispatch);
}

async function checkMfaEnrollment(storeDispatch: any) {
  const user = firebase.auth().currentUser;
  if (!user) {
    dispatch(storeDispatch, firebaseLogin("SUCCESS"));
    return;
  }
  try {
    await user.reload();
    const freshUser = firebase.auth().currentUser;
    const enrolledFactors = (freshUser as any)?.multiFactor?.enrolledFactors;
    if (enrolledFactors && enrolledFactors.length > 0) {
      dispatch(storeDispatch, firebaseLogin("SUCCESS"));
    } else {
      dispatch(storeDispatch, {
        actionName: Actions.MFA_ENROLLMENT_REQUIRED,
        body: { response: { message: "MFA enrollment required" } },
      });
    }
  } catch (e) {
    dispatch(storeDispatch, firebaseLogin("SUCCESS"));
  }
}
export async function getIdToken(forceRefresh?: boolean) {
  const currentUser = firebase.auth().currentUser;
  if (currentUser === null) {
    logout();
  }
  return currentUser?.getIdToken(forceRefresh) || "";
}
